import type {
  AcceptAdminInviteInput,
  AdminInviteInfo,
  AdminInviteInput,
  AdminUserListResponse,
  Partner,
  PartnerCreateInput,
  PartnerDetail,
  PartnerForm,
  PartnerFormUpdateInput,
  PartnerListResponse,
  PartnerUpdateInput,
  PublicPartner,
  AdminLoginInput,
  AdminLoginResponse,
  AdminOtpVerifyInput,
  AffiliateListQuery,
  AffiliateListResponse,
  AffiliateSettings,
  AffiliateSettingsUpdateInput,
  AgencyProptreeSettings,
  AgencyProptreeSettingsUpdateInput,
  AgencySettings,
  AgencySettingsInput,
  AgencyUserListResponse,
  AgentCreateInput,
  AgentCreateResponse,
  AgentListQuery,
  AgentListResponse,
  AgentProptreeSettings,
  AgentProptreeSettingsInput,
  AgentsProptreeResponse,
  AgentsProptreeSettingsUpdateInput,
  AliasCheckResponse,
  ChangePasswordInput,
  ClientListLightQuery,
  ClientListLightResponse,
  CrmChoice,
  CrmConnections,
  DashboardStats,
  DemoDataResponse,
  Enterprise,
  EnterpriseDetail,
  EnterpriseListQuery,
  EnterpriseListResponse,
  EnterpriseSettingsUpdateInput,
  ForgotPasswordInput,
  Form,
  FormCreateInput,
  FormListQuery,
  FormListResponse,
  FormUpdateInput,
  ImageUploadSignature,
  Impersonator,
  InquiryListQuery,
  InquiryListResponse,
  LeadRouting,
  ListingListQuery,
  ListingListResponse,
  LoginAsAgentInput,
  LoginAsAgentResponse,
  LoginAsInput,
  LoginAsResponse,
  LoginInput,
  OnboardingState,
  ProptreeListQuery,
  ProptreeListResponse,
  ResetPasswordInput,
  SaveBrandInput,
  SaveCrmInput,
  SaveRoleInput,
  SaveRoleResponse,
  ScrapeWebsiteInput,
  ScrapedWebsite,
  SignupInput,
  SyncLog,
  SyncLogListQuery,
  SyncLogListResponse,
  TransferRequest,
  TransferRequestApproveInput,
  TransferRequestListQuery,
  TransferRequestListResponse,
  UpdateProfileInput,
  UploadedFontFace,
  User,
  VerifySignupInput,
  ScraperRunListResponse,
} from '@proptr/shared';

export interface AuthSessionResponse {
  user: User;
  enterprise: Enterprise | null;
  impersonator?: Impersonator | null;
}

export interface CrmSyncStatus {
  lastPullAt: string;
  trigger: 'cron' | 'manual';
  durationMs: number;
  error: string | null;
  counts: {
    new: number;
    updated: number;
    removed: number;
    notSupported: number;
    errors: number;
  };
  reasons: Array<{ reason: string; count: number }>;
}

export interface BillingSummary {
  configured: boolean;
  plan: 'trial' | 'starter' | 'pro' | 'scale';
  status: string | null;
  renewsAt: string | null;
  hasCustomer: boolean;
  cancelAtPeriodEnd: boolean;
  affiliateAddon: number;
  affiliatesIncluded: number;
  affiliatesUsed: number;
  paymentMethod: {
    brand: string;
    last4: string;
    expMonth: number;
    expYear: number;
  } | null;
  invoices: Array<{
    id: string;
    number: string | null;
    total: number;
    currency: string;
    status: string | null;
    created: string;
    url: string | null;
    refunded: number;
  }>;
  creditNotes: Array<{
    id: string;
    number: string | null;
    invoiceNumber: string | null;
    total: number;
    currency: string;
    created: string;
    url: string | null;
  }>;
}

// Product feed (manager CRUD side of /api/feeds). Mirrors `serialize()`
// in apps/api/src/routes/feeds.ts. Kept here rather than in @proptr/shared
// because the API takes the body through untyped — nothing else consumes
// the shape.
export type { UploadedFontFace } from '@proptr/shared';

export interface FeedBrand {
  logoUrl: string | null;
  logoEnabled: boolean;
  logoSize: number;
  qrEnabled: boolean;
  qrSize: number;
  /** Normalised 0–1 top-left of the QR. null = template default. */
  qrPosition: { x: number; y: number } | null;
  /** QR placement for the 9:16 creative. Independent of the square. */
  qrPositionVertical: { x: number; y: number } | null;
  mainColor: string;
  secondaryColor: string;
  /** CSS stack. Used only when the matching *FontFace is null. */
  mainFont: string | null;
  secondaryFont: string | null;
  /** Uploaded typeface. `family` is generated, never filename-derived. */
  mainFontFace: UploadedFontFace | null;
  secondaryFontFace: UploadedFontFace | null;
}

export interface FeedFilters {
  type: 'all' | 'sale' | 'rent';
  plan: 'all' | 'on_plan' | 'off_plan';
  /** Empty array = no restriction. */
  areas: string[];
  propertyTypes: string[];
  bedrooms: string[];
  agents: string[];
  minPrice: number | null;
  maxPrice: number | null;
}

export interface FeedInput {
  name: string;
  template: string;
  /** Emit a 9:16 creative per listing alongside the square one. */
  includeVertical: boolean;
  brand: FeedBrand;
  filters: FeedFilters;
  /** Catalog vertical. Both emit CSV — 'meta' is home_listings, and
   *  'whatsapp' is products, the only vertical WhatsApp renders. */
  format: 'meta' | 'whatsapp';
  /** Whether the public URL serves anything. A disabled feed 404s but
   *  keeps its publicId, so pausing doesn't break the link the customer
   *  already pasted into Commerce Manager.
   *
   *  Optional on input: the editor doesn't own this field (the list's
   *  row menu does), and omitting it from a save must leave a paused
   *  feed paused rather than silently republishing it. */
  status?: 'live' | 'disabled';
}

export interface Feed extends FeedInput {
  _id: string;
  /** Always present on a response, unlike on input. */
  status: 'live' | 'disabled';
  publicId: string;
  /** Path only — resolve it against the API origin (`client.baseUrl`). */
  feedPath: string;
  createdAt: string | null;
  updatedAt: string | null;
  /** Listings this feed currently matches. Only returned by the list
   *  endpoint — create/update responses omit it. */
  listingCount?: number;
  /** When the CRM sync last changed a listing in this feed. Null when the
   *  feed matches nothing. List endpoint only. */
  lastSyncedAt?: string | null;
  /** Set once the feed has been published to a Meta catalog. */
  meta?: {
    businessId: string | null;
    catalogId: string;
    catalogName: string | null;
    publishedAt: string | null;
  } | null;
}

export interface FeedListResponse {
  items: Feed[];
  meta: { total: number };
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ClientOptions {
  baseUrl: string;
  /** When provided, the client sends `Authorization: Bearer <token>` on
   *  every request. Used by the manager app to carry an impersonation
   *  token in sessionStorage without touching the cookie. */
  getBearerToken?: () => string | null | undefined;
  /** Set by the admin app. Sent as `x-proptr-app` so the API reads the
   *  admin session cookie instead of the manager/agent one — lets both
   *  portals stay signed in side by side in one browser. */
  app?: 'admin';
}

export function createClient({ baseUrl, getBearerToken, app }: ClientOptions) {
  const request = async <T>(path: string, init?: RequestInit): Promise<T> => {
    const hasBody = init?.body != null;
    const incomingHeaders = (init?.headers ?? {}) as Record<string, string>;
    // Only send content-type when we actually have a body — Fastify rejects
    // empty bodies that claim to be JSON.
    const headers: Record<string, string> = { ...incomingHeaders };
    if (hasBody && !Object.keys(headers).some((k) => k.toLowerCase() === 'content-type')) {
      headers['content-type'] = 'application/json';
    }
    const bearer = getBearerToken?.();
    if (
      bearer &&
      !Object.keys(headers).some((k) => k.toLowerCase() === 'authorization')
    ) {
      headers['authorization'] = `Bearer ${bearer}`;
    }
    if (app) headers['x-proptr-app'] = app;
    const res = await fetch(`${baseUrl}${path}`, {
      credentials: 'include',
      ...init,
      headers,
    });
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      let message = body || res.statusText;
      let code: string | undefined;
      try {
        const parsed = JSON.parse(body) as { message?: string; code?: string };
        if (parsed.message) message = parsed.message;
        if (parsed.code) code = parsed.code;
      } catch {
        // body wasn't JSON; fall back to raw text
      }
      throw new ApiError(res.status, message, code);
    }
    if (res.status === 204) return undefined as T;
    return res.json() as Promise<T>;
  };

  return {
    request,
    baseUrl,
    auth: {
      // Self-serve signup creates a *pending* user and emails a verification
      // link. No session is established here — the user clicks the link,
      // which lands them on /onboarding and calls verifySignup() to activate.
      signup: (input: SignupInput) =>
        request<{ ok: true }>('/api/auth/signup', {
          method: 'POST',
          body: JSON.stringify(input),
        }),
      // Consumes the magic-link token, activates the user, and sets the auth
      // cookie. Returns the now-active session + the company website the user
      // entered at signup so the onboarding wizard can pre-fill it.
      verifySignup: (input: VerifySignupInput) =>
        request<
          AuthSessionResponse & {
            pendingCompanyWebsite: string | null;
            pendingCrm: CrmChoice | null;
          }
        >('/api/auth/verify-signup', {
          method: 'POST',
          body: JSON.stringify(input),
        }),
      login: (input: LoginInput) =>
        request<AuthSessionResponse>('/api/auth/login', {
          method: 'POST',
          body: JSON.stringify(input),
        }),
      logout: () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),
      me: () => request<AuthSessionResponse>('/api/auth/me'),
      forgotPassword: (input: ForgotPasswordInput) =>
        request<{ ok: true }>('/api/auth/forgot-password', {
          method: 'POST',
          body: JSON.stringify(input),
        }),
      resetPassword: (input: ResetPasswordInput) =>
        request<{ ok: true }>('/api/auth/reset-password', {
          method: 'POST',
          body: JSON.stringify(input),
        }),
      // Admin invite: who it's for (shown before the password is set),
      // then activate it with the chosen password.
      adminInvite: (token: string) =>
        request<AdminInviteInfo>(
          `/api/auth/admin/invite?token=${encodeURIComponent(token)}`,
        ),
      acceptAdminInvite: (input: AcceptAdminInviteInput) =>
        request<{ ok: true; email: string }>('/api/auth/admin/accept-invite', {
          method: 'POST',
          body: JSON.stringify(input),
        }),
      adminLogin: (input: AdminLoginInput) =>
        request<AdminLoginResponse>('/api/auth/admin/login', {
          method: 'POST',
          body: JSON.stringify(input),
        }),
      verifyAdminOtp: (input: AdminOtpVerifyInput) =>
        request<AuthSessionResponse>('/api/auth/admin/verify-otp', {
          method: 'POST',
          body: JSON.stringify(input),
        }),
      // Mint a manager bearer token for impersonation. The cookie is left
      // alone — the caller stashes the returned `impToken` in the new
      // tab's sessionStorage and uses it as the bearer there.
      loginAs: (input: LoginAsInput) =>
        request<LoginAsResponse>('/api/auth/admin/login-as', {
          method: 'POST',
          body: JSON.stringify(input),
        }),
      // Re-issue an admin cookie for the impersonator. The caller (the
      // manager tab) clears its sessionStorage bearer and navigates back
      // to the admin portal.
      exitImpersonation: () =>
        request<{ ok: true }>('/api/auth/admin/exit-impersonation', {
          method: 'POST',
        }),
      // Manager → agent equivalent of loginAs. Returns a bearer the new
      // agent-app tab stashes in sessionStorage. Manager's own cookie is
      // untouched, so the manager tab keeps working independently.
      loginAsAgent: (input: LoginAsAgentInput) =>
        request<LoginAsAgentResponse>('/api/auth/manager/login-as-agent', {
          method: 'POST',
          body: JSON.stringify(input),
        }),
      // Re-issue a manager cookie for a manager who used loginAsAgent.
      // Called from the agent-app's exit handler.
      managerExitImpersonation: () =>
        request<{ ok: true }>('/api/auth/manager/exit-impersonation', {
          method: 'POST',
        }),
    },
    onboarding: {
      // Each step calls this on mount to read the user's pending fields
      // (signup-time choices + scrape cache). Keeps step components
      // self-contained — no need for OnboardingPage to plumb data down.
      getState: () =>
        request<OnboardingState>('/api/onboarding/state'),
      // Step 1 of the onboarding wizard. Returns the cached scrape if we
      // have one — pass `{ force: true }` to bust the cache and re-call
      // Firecrawl. `cached: true` in the response lets the UI label the
      // result as fresh-from-disk vs. just-fetched.
      scrapeWebsite: (input: ScrapeWebsiteInput = {}) =>
        request<{ scrape: ScrapedWebsite; cached: boolean }>(
          '/api/onboarding/scrape-website',
          {
            method: 'POST',
            body: JSON.stringify(input),
          },
        ),
      // Step 1 — captures whether the user is a real manager or an agent
      // we'll reach out to. Server returns the next screen the wizard
      // should show: 'crm' (manager + listings), 'dashboard' (manager
      // skipping CRM), or 'agent-thank-you' (agent lead — auth cookie
      // is also cleared server-side).
      saveRole: (input: SaveRoleInput) =>
        request<SaveRoleResponse>('/api/onboarding/save-role', {
          method: 'POST',
          body: JSON.stringify(input),
        }),
      // Brand-basics step — persists the confirmed name/description/
      // colors/contact onto the enterprise's real branding fields. The
      // server also copies logo + favicon from its cached scrape.
      saveBrand: (input: SaveBrandInput) =>
        request<{ ok: true }>('/api/onboarding/save-brand', {
          method: 'POST',
          body: JSON.stringify(input),
        }),
      // Step 3 — persists the user's CRM choice + credentials (URL for
      // feed-based CRMs, apiKey + apiSecret for Property Finder).
      // Doesn't actually fetch listings yet; that's a future call once
      // the importer is wired up.
      saveCrm: (input: SaveCrmInput) =>
        request<{ ok: true }>('/api/onboarding/save-crm', {
          method: 'POST',
          body: JSON.stringify(input),
        }),
      // Marks the wizard as finished — flips the enterprise's
      // onboardingStatus from 'pending' to 'done'. Idempotent.
      complete: () =>
        request<{ ok: true; onboardingStatus: 'done' }>(
          '/api/onboarding/complete',
          { method: 'POST' },
        ),
    },
    // Manager-portal enterprise settings (lead routing, …). Tenant-scoped
    // to the caller's enterprise on the server.
    enterprise: {
      getLeadRouting: () =>
        request<LeadRouting>('/api/enterprise/lead-routing'),
      updateLeadRouting: (input: LeadRouting) =>
        request<LeadRouting>('/api/enterprise/lead-routing', {
          method: 'PUT',
          body: JSON.stringify(input),
        }),
      getCrmConnections: () =>
        request<CrmConnections>('/api/enterprise/crm-connections'),
      // Latest pull per connection: counts + why listings were held back.
      getCrmSyncStatus: () =>
        request<Record<string, CrmSyncStatus>>(
          '/api/enterprise/sync-status',
        ),
      // Master switch: does this brokerage sync a CRM at all? The current
      // value rides on the auth session's `enterprise.crmSyncEnabled`.
      updateCrmSync: (enabled: boolean) =>
        request<{ enabled: boolean }>('/api/enterprise/crm-sync', {
          method: 'PUT',
          body: JSON.stringify({ enabled }),
        }),
      // Marks the first-arrival welcome tour finished (enterprise-wide,
      // idempotent). The current state rides on `enterprise.welcomeTourDone`.
      completeWelcomeTour: () =>
        request<{ ok: true }>('/api/enterprise/welcome-tour-complete', {
          method: 'POST',
        }),
      // Manager self-service agency settings (branding + contact).
      getSettings: () =>
        request<AgencySettings>('/api/enterprise/settings'),
      updateSettings: (input: AgencySettingsInput) =>
        request<{ ok: true }>('/api/enterprise/settings', {
          method: 'PUT',
          body: JSON.stringify(input),
        }),
      updateCrmConnections: (input: CrmConnections) =>
        request<CrmConnections>('/api/enterprise/crm-connections', {
          method: 'PUT',
          body: JSON.stringify(input),
        }),
      // Sync history. List view returns SyncLogSummary[] (no changes
      // payload); detail returns the full SyncLog including changes.
      listSyncLogs: (params?: SyncLogListQuery) => {
        const qs = new URLSearchParams();
        if (params?.page) qs.set('page', String(params.page));
        if (params?.limit) qs.set('limit', String(params.limit));
        const q = qs.toString();
        return request<SyncLogListResponse>(
          `/api/enterprise/sync-logs${q ? `?${q}` : ''}`,
        );
      },
      getSyncLog: (id: string) =>
        request<SyncLog>(
          `/api/enterprise/sync-logs/${encodeURIComponent(id)}`,
        ),
      // Inbound transfer requests — agents whose phone we detected on
      // this brokerage's feed. List + approve (manager enters the code
      // the agent gave them).
      listTransferRequests: (params?: TransferRequestListQuery) => {
        const qs = new URLSearchParams();
        if (params?.status) qs.set('status', params.status);
        const q = qs.toString();
        return request<TransferRequestListResponse>(
          `/api/enterprise/transfer-requests${q ? `?${q}` : ''}`,
        );
      },
      approveTransferRequest: (
        id: string,
        input: TransferRequestApproveInput,
      ) =>
        request<TransferRequest>(
          `/api/enterprise/transfer-requests/${encodeURIComponent(id)}/approve`,
          { method: 'POST', body: JSON.stringify(input) },
        ),
    },
    // Endpoints scoped to the calling user (any authed role). Today
    // just the agent-side transfer-request handshake.
    me: {
      // Testing-mode dataset for the caller's brokerage. `source` is
      // 'agency' when their signup import landed, 'pending' while it is
      // still running, and 'static' for everything else — including every
      // failure, so callers never render an error off this.
      demoData: () => request<DemoDataResponse>('/api/me/demo-data'),
      // Update the calling user's own profile (name / avatar).
      updateProfile: (input: UpdateProfileInput) =>
        request<{ ok: true }>('/api/me/profile', {
          method: 'PUT',
          body: JSON.stringify(input),
        }),
      // Self-service password change (requires the current password).
      changePassword: (input: ChangePasswordInput) =>
        request<{ ok: true }>('/api/me/password', {
          method: 'PUT',
          body: JSON.stringify(input),
        }),
      listTransferRequests: (params?: TransferRequestListQuery) => {
        const qs = new URLSearchParams();
        if (params?.status) qs.set('status', params.status);
        const q = qs.toString();
        return request<TransferRequestListResponse>(
          `/api/me/transfer-requests${q ? `?${q}` : ''}`,
        );
      },
      dismissTransferRequest: (id: string) =>
        request<TransferRequest>(
          `/api/me/transfer-requests/${encodeURIComponent(id)}/dismiss`,
          { method: 'POST' },
        ),
    },
    admin: {
      listEnterprises: (params?: EnterpriseListQuery) => {
        const qs = new URLSearchParams();
        if (params?.search) qs.set('search', params.search);
        if (params?.page) qs.set('page', String(params.page));
        if (params?.limit) qs.set('limit', String(params.limit));
        const q = qs.toString();
        return request<EnterpriseListResponse>(
          `/api/admin/enterprises${q ? `?${q}` : ''}`,
        );
      },
      getEnterprise: (id: string) =>
        request<EnterpriseDetail>(
          `/api/admin/enterprises/${encodeURIComponent(id)}`,
        ),
      updateEnterprise: (id: string, patch: EnterpriseSettingsUpdateInput) =>
        request<{ ok: true }>(
          `/api/admin/enterprises/${encodeURIComponent(id)}`,
          {
            method: 'PATCH',
            body: JSON.stringify(patch),
          },
        ),
      listEnterpriseUsers: (id: string) =>
        request<AgencyUserListResponse>(
          `/api/admin/enterprises/${encodeURIComponent(id)}/users`,
        ),
      listScraperRuns: (
        scraperId: string,
        params?: { jobId?: string; page?: number; limit?: number },
      ) => {
        const qs = new URLSearchParams();
        if (params?.jobId) qs.set('jobId', params.jobId);
        if (params?.page) qs.set('page', String(params.page));
        if (params?.limit) qs.set('limit', String(params.limit));
        const q = qs.toString();
        return request<ScraperRunListResponse>(
          `/api/admin/scrapers/${encodeURIComponent(scraperId)}/runs${q ? `?${q}` : ''}`,
        );
      },
      // Admin console users — see apps/api/src/routes/adminUsers.ts.
      users: {
        list: () => request<AdminUserListResponse>('/api/admin/users'),
        invite: (input: AdminInviteInput) =>
          request<{ ok: true; id: string; emailSent: boolean }>('/api/admin/users', {
            method: 'POST',
            body: JSON.stringify(input),
          }),
        resendInvite: (id: string) =>
          request<{ ok: true; emailSent: boolean }>(
            `/api/admin/users/${encodeURIComponent(id)}/resend`,
            { method: 'POST' },
          ),
        remove: (id: string) =>
          request<{ ok: true; removed: 'invite' | 'admin' }>(
            `/api/admin/users/${encodeURIComponent(id)}`,
            { method: 'DELETE' },
          ),
      },
      // Partner buttons — see apps/api/src/routes/partners.ts.
      partners: {
        list: () => request<PartnerListResponse>('/api/admin/partners'),
        get: (id: string) =>
          request<PartnerDetail>(`/api/admin/partners/${encodeURIComponent(id)}`),
        create: (input: PartnerCreateInput) =>
          request<Partner>('/api/admin/partners', {
            method: 'POST',
            body: JSON.stringify(input),
          }),
        update: (id: string, patch: PartnerUpdateInput) =>
          request<PartnerDetail>(`/api/admin/partners/${encodeURIComponent(id)}`, {
            method: 'PATCH',
            body: JSON.stringify(patch),
          }),
        remove: (id: string) =>
          request<{ ok: true }>(`/api/admin/partners/${encodeURIComponent(id)}`, {
            method: 'DELETE',
          }),
        getForm: (id: string) =>
          request<PartnerForm>(
            `/api/admin/partners/${encodeURIComponent(id)}/form`,
          ),
        updateForm: (id: string, patch: PartnerFormUpdateInput) =>
          request<PartnerForm>(
            `/api/admin/partners/${encodeURIComponent(id)}/form`,
            { method: 'PUT', body: JSON.stringify(patch) },
          ),
        sendTestLead: (id: string) =>
          request<{ ok: boolean; status: number | string }>(
            `/api/admin/partners/${encodeURIComponent(id)}/test-lead`,
            { method: 'POST' },
          ),
      },
    },
    // Partner buttons this brokerage may switch on.
    partners: {
      listAvailable: () =>
        request<{ items: PublicPartner[] }>('/api/partners'),
    },
    billing: {
      summary: (opts?: { sync?: boolean; lite?: boolean }) => {
        const qs = new URLSearchParams();
        if (opts?.sync) qs.set('sync', '1');
        // lite = skip Stripe lookups (no payment method / invoices).
        if (opts?.lite) qs.set('lite', '1');
        const q = qs.toString();
        return request<BillingSummary>(
          `/api/billing/summary${q ? `?${q}` : ''}`,
        );
      },
      checkout: (plan?: 'starter' | 'pro' | 'scale') =>
        request<{ url: string }>('/api/billing/checkout', {
          method: 'POST',
          body: JSON.stringify({ plan }),
        }),
      previewChange: (change: { plan?: 'starter' | 'pro' | 'scale'; pack?: 0 | 5 | 10 | 20 | 30 }) =>
        request<{ dueNow: number; currency: string; newMonthly: number }>(
          '/api/billing/preview-change',
          { method: 'POST', body: JSON.stringify(change) },
        ),
      affiliateAddon: (pack: 0 | 5 | 10 | 20 | 30) =>
        request<{ ok: true; pack: number }>('/api/billing/affiliate-addon', {
          method: 'POST',
          body: JSON.stringify({ pack }),
        }),
      changePlan: (plan: 'starter' | 'pro' | 'scale') =>
        request<{ ok: true; plan: string }>('/api/billing/change-plan', {
          method: 'POST',
          body: JSON.stringify({ plan }),
        }),
      confirm: (sessionId: string) =>
        request<{ ok: true }>('/api/billing/confirm', {
          method: 'POST',
          body: JSON.stringify({ sessionId }),
        }),
      cancelPreview: () =>
        request<{ refundAmount: number; currency: string }>(
          '/api/billing/cancel-preview',
        ),
      cancel: () =>
        request<{ ok: true; refunded: number; currency: string }>(
          '/api/billing/cancel',
          { method: 'POST', body: JSON.stringify({}) },
        ),
      resume: () =>
        request<{ ok: true }>('/api/billing/resume', {
          method: 'POST',
          body: JSON.stringify({}),
        }),
      portal: () =>
        request<{ url: string }>('/api/billing/portal', {
          method: 'POST',
          body: JSON.stringify({}),
        }),
    },
    dashboard: {
      stats: (params?: { month?: number; year?: number }) => {
        const qs = new URLSearchParams();
        if (params?.month) qs.set('month', String(params.month));
        if (params?.year) qs.set('year', String(params.year));
        const q = qs.toString();
        return request<DashboardStats>(
          `/api/dashboard/stats${q ? `?${q}` : ''}`,
        );
      },
    },
    agents: {
      list: (params?: AgentListQuery) => {
        const qs = new URLSearchParams();
        if (params?.status) qs.set('status', params.status);
        if (params?.proptree) qs.set('proptree', params.proptree);
        if (params?.sort) qs.set('sort', params.sort);
        if (params?.order) qs.set('order', params.order);
        if (params?.page) qs.set('page', String(params.page));
        if (params?.limit) qs.set('limit', String(params.limit));
        const q = qs.toString();
        return request<AgentListResponse>(
          `/api/agents${q ? `?${q}` : ''}`,
        );
      },
      // Manager-creates-agent — basic profile fields. Server stamps
      // role + status + enterprise. 409 on email collision in same brokerage.
      create: (input: AgentCreateInput) =>
        request<AgentCreateResponse>('/api/agents', {
          method: 'POST',
          body: JSON.stringify(input),
        }),
      // Agent-self-service: read + patch the agent's own proptree
      // settings (Profile / Social / Buttons / Notifications / Recovery
      // email). Auth-gated to role==='agent' on the server.
      getMyProptree: () =>
        request<AgentProptreeSettings>('/api/agents/me/proptree'),
      updateMyProptree: (input: AgentProptreeSettingsInput) =>
        request<AgentProptreeSettings>('/api/agents/me/proptree', {
          method: 'PUT',
          body: JSON.stringify(input),
        }),
      // Live alias availability — UI debounces this on input change.
      checkAlias: (alias: string) => {
        const qs = new URLSearchParams({ alias });
        return request<AliasCheckResponse>(
          `/api/agents/me/proptree/check-alias?${qs.toString()}`,
        );
      },
      // Returns a short-lived Cloudinary signature so the browser can
      // POST the image file straight to Cloudinary. After upload, the
      // client persists the resulting URL via updateMyProptree.
      getProfileImageSignature: () =>
        request<ImageUploadSignature>(
          '/api/agents/me/proptree/image-signature',
          { method: 'POST' },
        ),
    },
    inquiries: {
      list: (params?: InquiryListQuery) => {
        const qs = new URLSearchParams();
        if (params?.qualification) qs.set('qualification', params.qualification);
        if (params?.rating) qs.set('rating', params.rating);
        if (params?.followUp) qs.set('followUp', params.followUp);
        if (params?.agent) qs.set('agent', params.agent);
        if (params?.listing) qs.set('listing', params.listing);
        if (params?.client) qs.set('client', params.client);
        if (params?.start) qs.set('start', params.start);
        if (params?.end) qs.set('end', params.end);
        if (params?.sort) qs.set('sort', params.sort);
        if (params?.order) qs.set('order', params.order);
        if (params?.page) qs.set('page', String(params.page));
        if (params?.limit) qs.set('limit', String(params.limit));
        const q = qs.toString();
        return request<InquiryListResponse>(
          `/api/inquiries${q ? `?${q}` : ''}`,
        );
      },
      exportCsv: async (params?: InquiryListQuery): Promise<Blob> => {
        const qs = new URLSearchParams();
        if (params?.qualification) qs.set('qualification', params.qualification);
        if (params?.rating) qs.set('rating', params.rating);
        if (params?.followUp) qs.set('followUp', params.followUp);
        if (params?.agent) qs.set('agent', params.agent);
        if (params?.listing) qs.set('listing', params.listing);
        if (params?.client) qs.set('client', params.client);
        if (params?.start) qs.set('start', params.start);
        if (params?.end) qs.set('end', params.end);
        if (params?.sort) qs.set('sort', params.sort);
        if (params?.order) qs.set('order', params.order);
        const q = qs.toString();
        const res = await fetch(
          `${baseUrl}/api/inquiries/export.csv${q ? `?${q}` : ''}`,
          { credentials: 'include' },
        );
        if (!res.ok) {
          const body = await res.text().catch(() => '');
          throw new ApiError(res.status, body || res.statusText);
        }
        return res.blob();
      },
    },
    clients: {
      listClientsLight: (params?: ClientListLightQuery) => {
        const qs = new URLSearchParams();
        if (params?.sort) qs.set('sort', params.sort);
        if (params?.order) qs.set('order', params.order);
        if (params?.page) qs.set('page', String(params.page));
        if (params?.limit) qs.set('limit', String(params.limit));
        const q = qs.toString();
        return request<ClientListLightResponse>(
          `/api/clients${q ? `?${q}` : ''}`,
        );
      },
    },
    proptrees: {
      listProptrees: (params?: ProptreeListQuery) => {
        const qs = new URLSearchParams();
        if (params?.status) qs.set('status', params.status);
        if (params?.sort) qs.set('sort', params.sort);
        if (params?.order) qs.set('order', params.order);
        if (params?.page) qs.set('page', String(params.page));
        if (params?.limit) qs.set('limit', String(params.limit));
        const q = qs.toString();
        return request<ProptreeListResponse>(
          `/api/proptrees${q ? `?${q}` : ''}`,
        );
      },
      // Agency-default proptree settings — used by the manager portal's
      // /proptree/agency view.
      getAgencyProptree: () =>
        request<AgencyProptreeSettings>('/api/proptrees/agency'),
      // Inherited `agents.*` settings + the list of preview-able agent
      // proptrees. Drives the manager portal's /proptree/agents view.
      getAgentsProptree: () =>
        request<AgentsProptreeResponse>('/api/proptrees/agency/agents'),
      // Partial update of the agents-defaults settings. Body keys mirror
      // the `settings` shape returned by getAgentsProptree.
      updateAgentsProptree: (input: AgentsProptreeSettingsUpdateInput) =>
        request<{ ok: true }>('/api/proptrees/agency/agents', {
          method: 'PUT',
          body: JSON.stringify(input),
        }),
      // Cloudinary signature for the agency proptree's folder. Same
      // direct-to-Cloudinary upload pattern as the agent endpoint.
      getAgencyImageSignature: () =>
        request<ImageUploadSignature>('/api/proptrees/agency/image-signature', {
          method: 'POST',
        }),
      listAffiliates: (params?: AffiliateListQuery) => {
        const qs = new URLSearchParams();
        if (params?.status) qs.set('status', params.status);
        if (params?.sort) qs.set('sort', params.sort);
        if (params?.order) qs.set('order', params.order);
        if (params?.page) qs.set('page', String(params.page));
        if (params?.limit) qs.set('limit', String(params.limit));
        const q = qs.toString();
        return request<AffiliateListResponse>(
          `/api/proptrees/affiliates${q ? `?${q}` : ''}`,
        );
      },
      getAffiliate: (id: string) =>
        request<AffiliateSettings>(`/api/proptrees/affiliates/${id}`),
      createAffiliate: () =>
        request<AffiliateSettings>('/api/proptrees/affiliates', {
          method: 'POST',
          body: JSON.stringify({}),
        }),
      cloneAffiliate: (sourceId: string, alias: string) =>
        request<AffiliateSettings>(
          `/api/proptrees/affiliates/${sourceId}/clone`,
          {
            method: 'POST',
            body: JSON.stringify({ alias }),
          },
        ),
      checkAffiliateAlias: (params: { alias: string; excludeId?: string }) => {
        const qs = new URLSearchParams({ alias: params.alias });
        if (params.excludeId) qs.set('excludeId', params.excludeId);
        return request<{ available: boolean }>(
          `/api/proptrees/check-alias?${qs.toString()}`,
        );
      },
      updateAgencyAlias: (alias: string) =>
        request<{ alias: string | null; shortId: string | null }>(
          '/api/proptrees/agency',
          {
            method: 'PUT',
            body: JSON.stringify({ alias }),
          },
        ),
      // Partial update of the agency-default doc (newsletter card today).
      // Same endpoint as updateAgencyAlias; only the sent keys are written.
      updateAgencyProptree: (input: AgencyProptreeSettingsUpdateInput) =>
        request<{ alias: string | null; shortId: string | null }>(
          '/api/proptrees/agency',
          {
            method: 'PUT',
            body: JSON.stringify(input),
          },
        ),
      updateAffiliate: (id: string, input: AffiliateSettingsUpdateInput) =>
        request<AffiliateSettings>(`/api/proptrees/affiliates/${id}`, {
          method: 'PUT',
          body: JSON.stringify(input),
        }),
      // Row-menu Enable/Disable. Enabling can 409 when the quota is full.
      setAffiliateStatus: (id: string, status: 'enabled' | 'disabled') =>
        request<{ _id: string; status: 'enabled' | 'disabled' }>(
          `/api/proptrees/affiliates/${id}/status`,
          {
            method: 'PATCH',
            body: JSON.stringify({ status }),
          },
        ),
    },
    listings: {
      list: (params?: ListingListQuery) => {
        const qs = new URLSearchParams();
        if (params?.status) qs.set('status', params.status);
        if (params?.type) qs.set('type', params.type);
        if (params?.sort) qs.set('sort', params.sort);
        if (params?.order) qs.set('order', params.order);
        if (params?.page) qs.set('page', String(params.page));
        if (params?.limit) qs.set('limit', String(params.limit));
        if (params?.externalIds) qs.set('externalIds', params.externalIds);
        const q = qs.toString();
        return request<ListingListResponse>(
          `/api/listings${q ? `?${q}` : ''}`,
        );
      },
    },
    forms: {
      list: (params?: FormListQuery) => {
        const qs = new URLSearchParams();
        if (params?.status) qs.set('status', params.status);
        if (params?.search) qs.set('search', params.search);
        if (params?.sort) qs.set('sort', params.sort);
        if (params?.order) qs.set('order', params.order);
        if (params?.page) qs.set('page', String(params.page));
        if (params?.limit) qs.set('limit', String(params.limit));
        const q = qs.toString();
        return request<FormListResponse>(`/api/forms${q ? `?${q}` : ''}`);
      },
      get: (id: string) => request<Form>(`/api/forms/${id}`),
      create: (input: FormCreateInput) =>
        request<Form>('/api/forms', {
          method: 'POST',
          body: JSON.stringify(input),
        }),
      update: (id: string, input: FormUpdateInput) =>
        request<Form>(`/api/forms/${id}`, {
          method: 'PUT',
          body: JSON.stringify(input),
        }),
      clone: (id: string) =>
        request<Form>(`/api/forms/${id}/clone`, { method: 'POST' }),
      disable: (id: string) =>
        request<Form>(`/api/forms/${id}/disable`, { method: 'PUT' }),
      enable: (id: string) =>
        request<Form>(`/api/forms/${id}/enable`, { method: 'PUT' }),
      remove: (id: string) =>
        request<{ ok: true }>(`/api/forms/${id}`, { method: 'DELETE' }),
    },
    feeds: {
      // No single-feed GET on the API — the list is enterprise-scoped and
      // small, so the editor hydrates by finding its id in it.
      list: () => request<FeedListResponse>('/api/feeds'),
      create: (input: FeedInput) =>
        request<Feed>('/api/feeds', {
          method: 'POST',
          body: JSON.stringify(input),
        }),
      // Partial: the route $sets whatever it's given, so a status toggle
      // sends one field rather than round-tripping the whole brand kit
      // and filters just to pause a feed.
      update: (id: string, input: Partial<FeedInput>) =>
        request<Feed>(`/api/feeds/${id}`, {
          method: 'PUT',
          body: JSON.stringify(input),
        }),
      remove: (id: string) =>
        request<{ ok: true }>(`/api/feeds/${id}`, { method: 'DELETE' }),
      /** Create a catalog in the brokerage's Meta business and register
       *  this feed's public URL against it. `businessId` is only needed
       *  when the connection covers more than one portfolio. */
      publishToMeta: (id: string, businessId?: string) =>
        request<{
          ok: true;
          feedUrl: string;
          meta: { catalogId: string; catalogName: string };
        }>(`/api/feeds/${id}/publish-meta`, {
          method: 'POST',
          body: JSON.stringify(businessId ? { businessId } : {}),
        }),
      /** Delete the catalog we created and forget it. `deleted` is false
       *  when the catalog was already gone on Meta's side — the local
       *  pointer is cleared either way. */
      unpublishFromMeta: (id: string) =>
        request<{ ok: true; deleted: boolean }>(
          `/api/feeds/${id}/publish-meta`,
          { method: 'DELETE' },
        ),
    },
    uploads: {
      // Mint a DO Spaces presigned-PUT URL plus the matching set of
      // `x-amz-*` headers the browser MUST forward verbatim on its
      // upload (the signature includes them). Also returns the public
      // CDN URL the manager should store on the doc.
      getSignature: (input: {
        // `agent-avatar` is uploaded before the agent doc exists so it
        // doesn't carry a proptreeId; the rest do.
        kind:
          | 'proptree-profile'
          | 'proptree-button'
          | 'agent-avatar'
          | 'user-avatar'
          | 'agency-logo'
          | 'agency-favicon'
          | 'brand-font'
          | 'brand-audio'
          // Admin-only; a partner button's logo.
          | 'partner-logo';
        proptreeId?: string;
        contentType:
          | 'image/jpeg'
          | 'image/png'
          | 'image/webp'
          // Accepted for agency-logo / agency-favicon only; the API
          // rejects it for any other kind.
          | 'image/svg+xml'
          | 'font/woff2'
          | 'font/woff'
          | 'font/ttf'
          | 'font/otf'
          // brand-audio only; the API rejects audio for other kinds.
          | 'audio/mpeg'
          | 'audio/mp4'
          | 'audio/wav';
        filename?: string;
      }) =>
        request<{
          uploadUrl: string;
          publicUrl: string;
          requiredHeaders: Record<string, string>;
        }>('/api/uploads/signature', {
          method: 'POST',
          body: JSON.stringify(input),
        }),
    },
  };
}

export type Client = ReturnType<typeof createClient>;
